/**
 * Roles and the capability gate — plan §16.0.
 *
 * A manager runs people and work; an admin runs the environment. A manager
 * cannot change a cap, a throttle threshold, a guard wordlist, a country rule,
 * a connector, a tenant or a sending domain — every one of those changes what
 * leaves the building for *everyone*.
 *
 * This module is the single source of truth for that matrix. Both Workers
 * import it: `actos-ui` to decide what to render, `actos-api` to decide what to
 * allow. The UI check is a courtesy; the API check is the control.
 */

export const ROLES = ['user', 'manager', 'admin'] as const;
export type Role = (typeof ROLES)[number];

/** Every gated capability in the product. Add here, not inline at call sites. */
export const CAPABILITIES = [
  // Work surfaces
  'workspace.sdr.own',
  'workspace.sdr.team',
  'workspace.marketing',

  /**
   * Approve a draft past a FAILING content guard ("Send anyway") — §6.1's
   * escape hatch. Admin-only by construction: listed here and granted to no
   * role explicitly, so only the admin's hold-everything set carries it. The
   * override is recorded on the approval event either way.
   */
  'task.approve.override',

  // Library (§16.2) — anyone drafts, only an admin approves
  'library.draft',
  'library.submit',
  'library.approve',
  'library.archive',

  // Campaigns (§6.2) — approver is manager or admin
  'campaign.build',
  'campaign.submit',
  'campaign.approve',

  // Routing (§8.1.1)
  'account.request',
  'account.assign',
  'account.unrouted.view',

  // Prospecting (§17.5)
  'prospect.run',

  // Suppression (§17.3)
  'suppression.view',
  'suppression.edit',
  'suppression.import',

  // Own settings (§16.1)
  'settings.own',
  'mailbox.connect.own',
  'mailbox.share.own',
  'mcp.connect.own',
  /**
   * Mint and revoke your own API tokens (plans/external-send-api.md §5.3). A
   * token acts as you and can only do less, so every role may hold one; the
   * admin dials decide what any token may do at all.
   */
  'api.token.own',

  // Admin — infrastructure (§16.3)
  'admin.users',
  'admin.tenants',
  'admin.domains',
  'admin.mailboxes.all',
  'admin.connectors',
  'admin.agents',
  /** Every token in the environment, and the power to revoke any of them. */
  'admin.api_tokens',

  // Admin — policy (§16.3 D). The blast radius is the whole environment.
  'admin.policy.scheduler',
  'admin.policy.caps',
  'admin.policy.throttle',
  'admin.policy.compliance',
  'admin.policy.guards',
  'admin.policy.tracking',
  'admin.policy.ranking',
  'admin.policy.ai',
  /** Whether the external API answers at all, and whether it may send unreviewed. */
  'admin.policy.api',

  // Admin — observability
  'admin.health',
  'admin.audit',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const USER_CAPS = [
  'workspace.sdr.own',
  'workspace.marketing',
  'library.draft',
  'library.submit',
  'campaign.build',
  'campaign.submit',
  'account.request',
  'prospect.run',
  'suppression.view',
  'settings.own',
  'mailbox.connect.own',
  'mailbox.share.own',
  'mcp.connect.own',
  'api.token.own',
] as const satisfies readonly Capability[];

const MANAGER_CAPS = [
  ...USER_CAPS,
  'workspace.sdr.team',
  'campaign.approve',
  'account.assign',
  'account.unrouted.view',
  'suppression.edit',
  'suppression.import',
] as const satisfies readonly Capability[];

/**
 * Admin holds everything. Stated as "all capabilities" rather than a list so a
 * newly added capability is never silently denied to admins.
 */
const ROLE_CAPS: Record<Role, ReadonlySet<Capability>> = {
  user: new Set<Capability>(USER_CAPS),
  manager: new Set<Capability>(MANAGER_CAPS),
  admin: new Set<Capability>(CAPABILITIES),
};

export function can(role: Role, capability: Capability): boolean {
  return ROLE_CAPS[role].has(capability);
}

export function capabilitiesFor(role: Role): Capability[] {
  return [...ROLE_CAPS[role]];
}

/** Throws rather than returning false — for use at API entry points. */
export class ForbiddenError extends Error {
  constructor(
    readonly role: Role,
    readonly capability: Capability,
  ) {
    super(`role '${role}' lacks capability '${capability}'`);
    this.name = 'ForbiddenError';
  }
}

export function assertCan(role: Role, capability: Capability): void {
  if (!can(role, capability)) throw new ForbiddenError(role, capability);
}

/**
 * Account visibility is three-state, not two — plan §8.1.1.
 *
 * Acceptance criterion 26 stays literally true in the `locked` state: an SDR
 * still cannot view or act on another SDR's *tasks*. Only the company row and
 * the owner's name are visible, so two reps never unknowingly work one logo.
 */
export type AccountVisibility = 'full' | 'requestable' | 'locked';

export function accountVisibility(
  role: Role,
  viewerUserId: string,
  assignedUserId: string | null,
): AccountVisibility {
  if (role === 'admin' || role === 'manager') return 'full';
  if (assignedUserId === null) return 'requestable';
  return assignedUserId === viewerUserId ? 'full' : 'locked';
}

/**
 * Would this patch leave the environment with no active admin?
 *
 * An environment with no admin cannot approve Library content, register a
 * connector, or complete its own setup checklist — and because actos-ui
 * refuses an inactive user's session, the sole admin deactivating themselves
 * is not a recoverable mistake. It is a lockout that only hand-written SQL
 * against production undoes.
 *
 * There are TWO ways to lose an admin — demote them, or deactivate them — and
 * the guard originally only asked about the first. So this takes the patch and
 * asks what state it would PRODUCE, rather than trying to recognise the shape
 * of a demotion.
 *
 * `activeAdminCount` includes the target when the target is currently an
 * active admin; the caller counts rows, this decides.
 */
export function wouldOrphanEnvironment(
  target: { role: Role; active: boolean },
  patch: { role?: Role; active?: boolean },
  activeAdminCount: number,
): boolean {
  const isActiveAdmin = target.role === 'admin' && target.active;
  if (!isActiveAdmin) return false;

  const stillActiveAdmin = (patch.role ?? target.role) === 'admin' && (patch.active ?? target.active);
  if (stillActiveAdmin) return false;

  return activeAdminCount <= 1;
}
